/**
 * QORYN GTM Command Center — API client + Supabase read/realtime helpers.
 *
 * Reads go through supabase-js (RLS-scoped SELECT); the /api/gtm/status
 * endpoint is the only mutation surface (founder-gated server-side).
 */
import { supabase, getAccessToken } from "@/lib/supabaseClient";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

async function authHeader() {
    const tok = await getAccessToken();
    return tok ? { Authorization: `Bearer ${tok}` } : {};
}

// ---------------------------------------------------------------------------
// Reads via supabase-js (RLS SELECT)
// ---------------------------------------------------------------------------

export async function fetchMilestonesSupabase() {
    const { data, error } = await supabase
        .from("gtm_milestones")
        .select("*")
        .order("sort_order", { ascending: true });
    if (error) throw error;
    return data ?? [];
}

export async function fetchEventsSupabase({ limit = 50 } = {}) {
    const { data, error } = await supabase
        .from("gtm_status_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);
    if (error) throw error;
    return data ?? [];
}

// ---------------------------------------------------------------------------
// Backend fallbacks (also used to fetch derived readiness + founder flag)
// ---------------------------------------------------------------------------

export async function fetchMe() {
    const r = await fetch(`${API}/gtm/me`, { headers: { ...(await authHeader()) } });
    if (!r.ok) throw new Error(`gtm.me failed (${r.status})`);
    return r.json();
}

export async function fetchMilestonesApi() {
    const r = await fetch(`${API}/gtm/milestones`, { headers: { ...(await authHeader()) } });
    if (!r.ok) throw new Error(`gtm.milestones failed (${r.status})`);
    const body = await r.json();
    return body.milestones;
}

export async function fetchEventsApi({ limit = 50 } = {}) {
    const r = await fetch(`${API}/gtm/events?limit=${limit}`, { headers: { ...(await authHeader()) } });
    if (!r.ok) throw new Error(`gtm.events failed (${r.status})`);
    const body = await r.json();
    return body.events;
}

export async function fetchReadinessApi() {
    const r = await fetch(`${API}/gtm/readiness`, { headers: { ...(await authHeader()) } });
    if (!r.ok) throw new Error(`gtm.readiness failed (${r.status})`);
    return r.json();
}

// ---------------------------------------------------------------------------
// Founder-only mutation
// ---------------------------------------------------------------------------

export async function postStatusUpdate({
    milestone, status, note = null,
    current_blocker = null, next_action = null,
    source = "MANUAL_FOUNDER_UPDATE",
}) {
    const r = await fetch(`${API}/gtm/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({ milestone, status, note, current_blocker, next_action, source }),
    });
    if (!r.ok) {
        let detail;
        const raw = await r.text(); try { detail = JSON.parse(raw); } catch { detail = { message: raw }; }
        const err = new Error(`gtm.status POST failed (${r.status})`);
        err.status = r.status;
        err.detail = detail;
        throw err;
    }
    return r.json();
}

// ---------------------------------------------------------------------------
// Realtime — supabase channel
// Callers subscribe to both tables and receive raw postgres_changes
// payloads; components choose whether to refetch or merge locally.
// ---------------------------------------------------------------------------

export function subscribeGtm({ onMilestone, onEvent }) {
    const channel = supabase
        .channel("qoryn.gtm.v1")
        .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "gtm_milestones" },
            (payload) => { try { onMilestone?.(payload); } catch { /* noop */ } },
        )
        .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "gtm_status_events" },
            (payload) => { try { onEvent?.(payload); } catch { /* noop */ } },
        )
        .subscribe();
    return () => { try { supabase.removeChannel(channel); } catch { /* noop */ } };
}
